import type { CurrencyCode } from './model';

/**
 * Text typed or pasted into an amount field → number.
 *
 * Accepts what people actually type into a rate: grouping separators
 * (`1,250`, `1 250`, `1'250`), a currency code or symbol around the number
 * (`£850`, `CAD 850`, `850 €`) and either `.` or `,` as the decimal point.
 * Anything else is `invalid`, never silently `empty`, so a field can tell
 * "nothing typed" from "typed something unreadable".
 */
export type MoneyInput =
  | { type: 'empty' }
  | { type: 'invalid' }
  | { type: 'number'; value: number; fractionDigits: number };

export type DecimalSeparator = '.' | ',';

export type ParseMoneyInputOptions = {
  /**
   * Settles the one ambiguous shape: a single separator followed by exactly
   * three digits (`1,250`, `1.250`). It is a decimal point only when it is
   * the locale's decimal separator; otherwise it groups thousands.
   */
  decimalSeparator: DecimalSeparator;
  /**
   * The currency of the amount: its code or symbol may stand before or after
   * the number (`CAD 850`, `CA$850`). Another currency, or any text when there
   * is no currency (a plain number field), is `invalid`.
   */
  currency?: CurrencyCode;
};

const SIGN = /^[-−]/;
/** Spaces (also non-breaking) and apostrophes; they may only group thousands. */
const GROUPING_CHAR = /[\s'’]/;
const GROUPING_CHARS = /[\s'’]/g;
const SPACE_GROUPED = /^\d{1,3}(?:[\s'’]\d{3})+(?:[.,]\d*)?$/;
const DIGITS_AND_SEPARATORS = /^[\d.,]*\d[\d.,]*$/;
const PLAIN_INTEGER = /^\d*$/;
const GROUPED_INTEGER = /^\d{1,3}([.,])\d{3}(?:\1\d{3})*$/;
const LETTER = /[A-Z]/;

/** What may mark the currency: `CAD`, `CA$`, `C$`, `$` — longest first. */
function currencyMarks(currency: CurrencyCode): string[] {
  const code = currency.toUpperCase();
  const marks = new Set([code]);
  for (const currencyDisplay of ['symbol', 'narrowSymbol'] as const) {
    try {
      const symbol = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: code,
        currencyDisplay
      })
        .formatToParts(0)
        .find(part => part.type === 'currency')
        ?.value.toUpperCase();
      if (!symbol) continue;
      marks.add(symbol);
      // A bare `$` of CAD is also written `C$` or `CA$`.
      if (symbol.length === 1) {
        for (let length = 1; length < code.length; length++) {
          marks.add(code.slice(0, length) + symbol);
        }
      }
    } catch {
      // A code Intl does not know has no symbol.
    }
  }
  return [...marks].sort((a, b) => b.length - a.length);
}

/** The text without the currency's mark before or after the number. */
function stripCurrency(text: string, currency: CurrencyCode): string {
  const marks = currencyMarks(currency);
  const upper = text.toUpperCase();
  // A code must not run into further letters: `CADX` is not `CAD`.
  const leading = marks.find(
    mark =>
      upper.startsWith(mark) &&
      !(LETTER.test(mark.slice(-1)) && LETTER.test(upper.charAt(mark.length)))
  );
  const rest = leading ? text.slice(leading.length).trim() : text;
  const restUpper = rest.toUpperCase();
  const trailing = marks.find(
    mark =>
      restUpper.endsWith(mark) &&
      !(
        LETTER.test(mark.charAt(0)) &&
        LETTER.test(restUpper.charAt(rest.length - mark.length - 1))
      )
  );
  return trailing ? rest.slice(0, rest.length - trailing.length).trim() : rest;
}

function count(text: string, char: string): number {
  return text.split(char).length - 1;
}

/** The separator that starts the fraction, or `null` when there is none. */
function decimalSeparatorIn(
  text: string,
  localeSeparator: DecimalSeparator
): DecimalSeparator | null {
  const commas = count(text, ',');
  const dots = count(text, '.');
  if (commas > 0 && dots > 0) {
    return text.lastIndexOf(',') > text.lastIndexOf('.') ? ',' : '.';
  }
  if (commas + dots !== 1) return null;
  const separator: DecimalSeparator = commas === 1 ? ',' : '.';
  const digitsAfter = text.length - text.indexOf(separator) - 1;
  if (digitsAfter === 3) {
    return separator === localeSeparator ? separator : null;
  }
  return separator;
}

export function parseMoneyInput(
  raw: string,
  options: ParseMoneyInputOptions
): MoneyInput {
  let text = raw.trim();
  if (text === '') return { type: 'empty' };

  let negative = false;
  if (SIGN.test(text)) {
    negative = true;
    text = text.slice(1).trim();
  }
  if (options.currency) text = stripCurrency(text, options.currency);
  if (!negative && SIGN.test(text)) {
    negative = true;
    text = text.slice(1).trim();
  }
  if (GROUPING_CHAR.test(text)) {
    if (!SPACE_GROUPED.test(text)) return { type: 'invalid' };
    text = text.replace(GROUPING_CHARS, '');
  }
  if (!DIGITS_AND_SEPARATORS.test(text)) return { type: 'invalid' };

  const separator = decimalSeparatorIn(text, options.decimalSeparator);
  const cut = separator === null ? text.length : text.lastIndexOf(separator);
  const integerPart = text.slice(0, cut);
  const fractionPart = separator === null ? '' : text.slice(cut + 1);
  if (!/^\d*$/.test(fractionPart)) return { type: 'invalid' };
  if (!PLAIN_INTEGER.test(integerPart) && !GROUPED_INTEGER.test(integerPart)) {
    return { type: 'invalid' };
  }

  const digits = integerPart.replace(/[.,]/g, '') || '0';
  const magnitude = Number(
    fractionPart === '' ? digits : `${digits}.${fractionPart}`
  );
  if (!Number.isFinite(magnitude)) return { type: 'invalid' };
  return {
    type: 'number',
    value: negative && magnitude !== 0 ? -magnitude : magnitude,
    fractionDigits: fractionPart.length
  };
}

/** Decimal separator of a locale (`pl` → `,`); a dot when Intl rejects it. */
export function decimalSeparatorOf(locale: string): DecimalSeparator {
  try {
    const decimal = new Intl.NumberFormat(locale)
      .formatToParts(1.5)
      .find(part => part.type === 'decimal');
    return decimal?.value === ',' ? ',' : '.';
  } catch {
    return '.';
  }
}
